import { type Ref, type ComputedRef } from "vue";
import { isEqual } from "lodash";
import { useComplexModelModelWithChanges } from "@/features/core-shared/composables/useComplexModelWithChanges";
import { useVenuesByCityMasterdata } from "@/features/masterdata/composables/useVenuesByCityMasterdata";
import type { City as GeoCity, Venue } from "@/types/generated/GeoMasterDataApi";

/**
 * Factory for the venues field.
 *
 * @param initialValue - reactive ref of venue codes from the data source
 * @param cityModel - the current city selection (for filtering venues by city)
 * @param additionalCitiesModel - the current additional cities selection
 */
export function useVenuesField(
  initialValue: Ref<string[]>,
  cityModel: ComputedRef<GeoCity | null> | Ref<GeoCity | null>,
  additionalCitiesModel: ComputedRef<GeoCity[]> | Ref<GeoCity[]>
) {
  const { model, hasChanges, isFilled, reset } = useComplexModelModelWithChanges<string[]>(initialValue);

  const cityCodes = computed(() => {
    const codes: string[] = [];
    if (cityModel.value?.code) codes.push(cityModel.value.code);
    additionalCitiesModel.value.forEach((c) => {
      if (c.code) codes.push(c.code);
    });
    return codes;
  });

  const { venues: venuesList, isLoading } = useVenuesByCityMasterdata(cityCodes);

  const selectedVenueList = computed<Venue[]>(() =>
    model.value.map((code) => venuesList.value?.find((v) => v.code === code)).filter((v): v is Venue => v !== undefined)
  );

  function handleRemoveVenue(venue: Venue) {
    model.value = model.value.filter((v) => v !== venue.code);
  }

  // Remove venues that no longer belong to a selected city.
  // We track whether the user has changed cities (vs the initial load)
  // and watch venuesList so the filter runs only once the query has
  // resolved with fresh data for the new cityCodes.
  // If cityCodes is already populated at mount, it's already settled —
  // the first user-initiated change should trigger venue filtering.
  let hasCityCodesSettled = cityCodes.value.length > 0;

  watch(cityCodes, (newValue, oldValue) => {
    if (isEqual(newValue, oldValue)) return;

    if (!hasCityCodesSettled) {
      // First real transition — masterdata resolving, not a user action
      hasCityCodesSettled = true;
    }
  });

  watch(venuesList, (venues) => {
    if (!hasCityCodesSettled || isLoading.value || model.value.length === 0) return;
    model.value = model.value.filter((code) =>
      venues.some((venue) => venue.code === code && cityCodes.value.includes(venue.city))
    );
  });

  return {
    id: "location.venues" as const,
    model,
    venuesList,
    selectedVenueList,
    cityCodes,
    hasChanges,
    isFilled,
    isLoading,
    handleRemoveVenue,
    reset,
  };
}
